import { useState } from "react";

const FAQS = [
  {
    question: "How do the fuel price predictions work?",
    answer:
      "We collect daily Basic Fuel Price (BFP) recovery data published by the Central Energy Fund (CEF). Our forecasting model calculates a recency-weighted moving average across the 22-day pricing cycle to project the expected pump-price adjustment at the start of the next month.",
  },
  {
    question: "Are these forecasts official government prices?",
    answer:
      "No. Forecasts are estimates based on daily recovery data. The official regulated fuel prices are announced by the Department of Mineral and Petroleum Resources (DMPR) on the first Monday or Tuesday of each month and take effect on the first Wednesday.",
  },
  {
    question: "When are the source reports updated?",
    answer:
      "The CEF publishes daily recovery reports on South African business days (Monday through Friday), typically between 10:00 AM and 2:00 PM SAST. Our automated AWS cloud bot checks for updates daily and recalculates predictions automatically.",
  },
  {
    question: "How reliable is this web app and its confidence score?",
    answer:
      "Our algorithm evaluates confidence based on two factors: cycle progress (coverage of the 22 business days) and signal stability. Early in the cycle, confidence is low to medium; near cycle completion (over 80% coverage), confidence reaches High.",
  },
  {
    question: "Why might the official price change differ slightly from daily predictions?",
    answer:
      "Daily CEF figures track international oil prices and the USD/ZAR exchange rate. Official DMPR monthly adjustments also include statutory fuel levy changes, retail or wholesale margin tweaks, or Slate Account balance adjustments which are not part of daily recovery rates.",
  },
  {
    question: "What fuel products are tracked?",
    answer:
      "We track all five primary regulated fuels: Petrol 95, Petrol 93, Diesel 500ppm, Diesel 50ppm, and Illuminating Paraffin.",
  },
] as const;

function ChevronIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path
        d="m5 7.5 5 5 5-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function FAQSection() {
  const [openItems, setOpenItems] = useState<Set<number>>(() => new Set([0]));

  function toggleItem(index: number) {
    setOpenItems((current) => {
      const next = new Set(current);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return (
    <section className="faq-section" id="faq" aria-labelledby="faq-heading">
      <div className="faq-intro">
        <span className="eyebrow">Frequently asked questions</span>
        <h2 id="faq-heading">Fuel pricing, explained clearly.</h2>
        <p>
          Understand the data, timing, and limitations behind each forecast before
          planning your next fill-up.
        </p>
      </div>

      <div className="faq-list">
        {FAQS.map((faq, index) => {
          const isOpen = openItems.has(index);
          const questionId = `faq-question-${index}`;
          const answerId = `faq-answer-${index}`;

          return (
            <article className={`faq-item${isOpen ? " open" : ""}`} key={faq.question}>
              <h3>
                <button
                  type="button"
                  id={questionId}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => toggleItem(index)}
                >
                  <span className="faq-number" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="faq-question">{faq.question}</span>
                  <span className="faq-chevron" aria-hidden="true">
                    <ChevronIcon />
                  </span>
                </button>
              </h3>
              <div className="faq-answer-grid" data-open={isOpen}>
                <div>
                  <div
                    className="faq-answer"
                    id={answerId}
                    role="region"
                    aria-labelledby={questionId}
                    aria-hidden={!isOpen}
                  >
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
